"use client";

import { useLang } from "@/contexts/language-context";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChefHat, Sparkles } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

type Phase = "idle" | "select" | "order" | "fire";

const TABLES = [
  { id: 1, x: 12, y: 18, seats: 2, state: "open" as const },
  { id: 2, x: 32, y: 22, seats: 4, state: "seated" as const },
  { id: 3, x: 56, y: 16, seats: 4, state: "paying" as const },
  { id: 4, x: 80, y: 24, seats: 6, state: "dirty" as const },
  { id: 12, x: 22, y: 56, seats: 4, state: "seated" as const, hero: true },
  { id: 6, x: 50, y: 60, seats: 2, state: "open" as const },
  { id: 7, x: 74, y: 58, seats: 4, state: "check" as const },
  { id: 8, x: 14, y: 84, seats: 2, state: "open" as const },
  { id: 9, x: 44, y: 88, seats: 4, state: "seated" as const },
  { id: 10, x: 72, y: 86, seats: 6, state: "open" as const },
];

const STATE_COLOR: Record<string, string> = {
  open: "bg-white border-redy-charcoal/10",
  seated: "bg-redy-orange/15 border-redy-orange/40",
  check: "bg-redy-charcoal text-white border-redy-charcoal",
  paying: "bg-redy-red/15 border-redy-red/40",
  dirty: "bg-black/10 border-black/15",
};

const ITEMS = [
  { name: "Caesar Salad", price: 14, course: "app" },
  { name: "Ribeye 14oz", price: 38, course: "main" },
  { name: "Linguine Vongole", price: 26, course: "main" },
  { name: "House Cab", price: 14, course: "drink" },
];

const STEP_MS = 1100;

export default function RestaurantsDineHero() {
  const { t } = useLang();
  const [phase, setPhase] = useState<Phase>("idle");
  const [itemIdx, setItemIdx] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    const cycle = () => {
      setPhase("idle");
      setItemIdx(0);
      timers.push(setTimeout(() => setPhase("select"), STEP_MS));
      timers.push(setTimeout(() => setPhase("order"), STEP_MS * 2));
      ITEMS.forEach((_, i) => {
        timers.push(setTimeout(() => setItemIdx(i + 1), STEP_MS * 2.4 + i * 600));
      });
      timers.push(setTimeout(() => setPhase("fire"), STEP_MS * 2.4 + ITEMS.length * 600 + 400));
      timers.push(setTimeout(cycle, STEP_MS * 2.4 + ITEMS.length * 600 + 2400));
    };
    cycle();
    return () => timers.forEach(clearTimeout);
  }, []);

  const visibleItems = ITEMS.slice(0, itemIdx);
  const subtotal = visibleItems.reduce((a, b) => a + b.price, 0);

  return (
    <section className="relative pt-28 pb-24 px-6 overflow-hidden bg-[#FAFAFA]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/solution-restaurants-dinein.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAFA]/80 pointer-events-none" />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-redy-orange/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[450px] h-[450px] bg-redy-red/6 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-white border border-black/5 px-4 py-2 rounded-full text-xs font-bold text-redy-charcoal shadow-sm">
            <Sparkles className="w-3 h-3 text-redy-orange fill-redy-orange" />
            {t("Dine-in Restaurants", "Restaurantes Dine-in")}
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center text-[44px] sm:text-5xl md:text-[72px] font-black leading-[1.02] tracking-tight mb-6 max-w-[900px] mx-auto"
        >
          <span className="text-redy-charcoal">{t("Floor to fork.", "Del piso al plato.")}</span>
          <br />
          <span className="redy-text-gradient">{t("In one tap.", "En un tap.")}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center text-lg md:text-xl text-redy-secondary max-w-[640px] mx-auto leading-relaxed mb-10 font-medium"
        >
          {t(
            "Full-service POS with a visual floor map, tableside ordering on iPad, 4-way bill splits, and multi-station KDS that fires courses in the right order.",
            "POS de servicio completo con mapa visual del salón, órdenes tableside en iPad, splits de 4 vías y KDS multi-estación que dispara cursos en el orden correcto."
          )}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex flex-col sm:flex-row gap-3 justify-center mb-16"
        >
          <Link
            href="/auth/sign-up"
            className="inline-flex items-center justify-center gap-2 bg-redy-charcoal hover:bg-black text-white px-7 py-4 rounded-full font-bold text-sm transition-all hover:scale-[1.02]"
          >
            {t("Start free →", "Empieza gratis →")}
          </Link>
          <Link
            href="/pricing"
            className="inline-flex items-center justify-center gap-2 bg-white border border-black/10 hover:border-black/30 text-redy-charcoal px-7 py-4 rounded-full font-bold text-sm transition-all"
          >
            {t("See pricing", "Ver precios")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        {/* Floor map + tableside iPad */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative mx-auto max-w-[960px]"
        >
          <div className="relative bg-white border border-black/[0.06] rounded-[28px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.18)] p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-6">
              {/* Floor map */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-redy-charcoal/50 mb-3 flex items-center justify-between">
                  <span>{t("Floor map · Friday 7:42 PM", "Mapa · Viernes 7:42 PM")}</span>
                  <span className="text-redy-orange">
                    {phase === "idle"
                      ? t("Tap a table", "Toca una mesa")
                      : t("Table 12 · 4 guests", "Mesa 12 · 4 invitados")}
                  </span>
                </div>
                <div className="relative aspect-[5/4] bg-[#FBF9F4] rounded-2xl border border-black/[0.06] overflow-hidden">
                  {/* Subtle floor grid */}
                  <div
                    className="absolute inset-0 opacity-[0.04]"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)",
                      backgroundSize: "20px 20px",
                    }}
                  />
                  {/* Hero ring around table 12 */}
                  <AnimatePresence>
                    {(phase === "select" || phase === "order" || phase === "fire") && (
                      <motion.div
                        layoutId="ring"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        style={{
                          left: `${TABLES[4].x - 8}%`,
                          top: `${TABLES[4].y - 9}%`,
                        }}
                        className="absolute w-[80px] h-[80px] rounded-full ring-2 ring-redy-orange ring-offset-2 ring-offset-[#FBF9F4]"
                      />
                    )}
                  </AnimatePresence>
                  {TABLES.map((tbl) => (
                    <div
                      key={tbl.id}
                      style={{ left: `${tbl.x}%`, top: `${tbl.y}%` }}
                      className="absolute -translate-x-1/2 -translate-y-1/2"
                    >
                      <motion.div
                        animate={{
                          scale: tbl.hero && phase === "select" ? [1, 1.15, 1] : 1,
                        }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className={`w-16 h-12 rounded-xl border-2 flex flex-col items-center justify-center font-mono text-xs font-black shadow-[0_2px_6px_rgba(0,0,0,0.06)] ${
                          STATE_COLOR[tbl.state]
                        }`}
                      >
                        <span className={tbl.state === "check" ? "text-white" : "text-redy-charcoal"}>
                          {tbl.id}
                        </span>
                        <span
                          className={`text-[8px] font-bold ${
                            tbl.state === "check" ? "text-white/70" : "text-redy-charcoal/50"
                          }`}
                        >
                          {tbl.seats}p
                        </span>
                      </motion.div>
                    </div>
                  ))}
                </div>
                {/* Legend */}
                <div className="mt-3 flex flex-wrap gap-3 text-[10px] font-bold text-redy-charcoal/60 uppercase tracking-wider">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-white border border-redy-charcoal/20 rounded" />
                    {t("Open", "Libre")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-redy-orange/15 border border-redy-orange/40 rounded" />
                    {t("Seated", "Sentados")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-redy-charcoal rounded" />
                    {t("Check dropped", "Cuenta entregada")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 bg-redy-red/15 border border-redy-red/40 rounded" />
                    {t("Paying", "Pagando")}
                  </span>
                </div>
              </div>

              {/* Tableside iPad */}
              <div className="relative">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-redy-charcoal/50 mb-3">
                  {t("Tableside iPad", "iPad tableside")}
                </div>
                <div className="bg-redy-charcoal rounded-[20px] p-3">
                  <div className="bg-[#FDFCFA] rounded-[14px] p-4 min-h-[340px] flex flex-col">
                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/60 mb-3">
                      <span>{t("Server · Maya", "Mesero · Maya")}</span>
                      <span>
                        {phase === "idle"
                          ? "—"
                          : phase === "select"
                          ? t("Table 12", "Mesa 12")
                          : phase === "order"
                          ? t("Building order…", "Armando orden…")
                          : t("Fired ✓", "Disparada ✓")}
                      </span>
                    </div>
                    <div className="flex-1 space-y-2">
                      {visibleItems.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-redy-charcoal/40 font-medium italic">
                          {phase === "select"
                            ? t("Greet guests…", "Saludando invitados…")
                            : t("Empty — pick a table", "Vacío — elige una mesa")}
                        </div>
                      ) : (
                        visibleItems.map((it, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center justify-between text-sm text-redy-charcoal"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                  it.course === "drink"
                                    ? "bg-redy-blue/10 text-redy-blue"
                                    : it.course === "app"
                                    ? "bg-redy-orange/10 text-redy-orange"
                                    : "bg-redy-charcoal/10 text-redy-charcoal"
                                }`}
                              >
                                {it.course}
                              </span>
                              <span className="font-medium">{it.name}</span>
                            </div>
                            <span className="font-mono tabular-nums">${it.price}</span>
                          </motion.div>
                        ))
                      )}
                    </div>
                    {visibleItems.length > 0 && (
                      <div className="border-t border-black/5 mt-3 pt-3 space-y-1 text-sm">
                        <div className="flex justify-between text-redy-charcoal/60">
                          <span>{t("Subtotal", "Subtotal")}</span>
                          <span className="font-mono tabular-nums">${subtotal}</span>
                        </div>
                        <AnimatePresence>
                          {phase === "fire" && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0 }}
                              className="mt-2 inline-flex items-center gap-2 bg-redy-orange text-white px-3 py-1.5 rounded-full text-[11px] font-bold"
                            >
                              <ChefHat className="w-3.5 h-3.5" />
                              {t("Fired to kitchen", "Enviado a cocina")}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Stat strip */}
            <div className="mt-6 pt-5 border-t border-black/5 grid grid-cols-3 gap-3 text-center">
              <Stat label={t("Course firing", "Cursos")} value="✓" />
              <Stat label={t("Tableside ordering", "Orden tableside")} value="✓" />
              <Stat label={t("Card fees", "Fees de tarjeta")} value={t("Covered", "Cubiertos")} />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-mono tabular-nums text-lg font-black text-redy-charcoal">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-redy-charcoal/50 mt-0.5">
        {label}
      </div>
    </div>
  );
}
